import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';

type AppState = {
  cart: Cart;
  setCart: Dispatch<SetStateAction<Cart>>;
};

export type CartProduct = {
  name: string;
  databaseId: number;
  sku: string;
  price: string;
  uri: string;
  image?: { sourceUrl: string };
};

export type AvailableShippingMethodRate = {
  cost: string;
  id: string;
  label: string;
};

export type AvailableShippingMethod = {
  /**
   * Null until WooCommerce has calculated rates for the cart. A cart saved in that
   * state persists to localStorage and comes back null on the next visit, so every
   * read has to cope with it.
   */
  rates: AvailableShippingMethodRate[] | null;
};

export type Cart = {
  total: string;
  contentsTotal: string;
  shippingTotal: string;
  shippingTax: string;
  discountTotal: string;
  discountTax: string;
  availableShippingMethods: AvailableShippingMethod[];
  chosenShippingMethods: string[];
  contents: {
    itemCount: number;
    nodes: {
      key: string;
      quantity: number;
      product: { node: CartProduct };
    }[];
  };
};

export const emptyCart: Cart = {
  total: '',
  shippingTotal: '',
  shippingTax: '',
  contentsTotal: '',
  discountTotal: '',
  discountTax: '',
  // Matches what the server returns for an empty basket. It previously carried a
  // placeholder rate with an empty id, which rendered a selectable "No shipping"
  // option on checkout — visible after signing out, since that resets to this value.
  availableShippingMethods: [],
  chosenShippingMethods: [],
  contents: {
    itemCount: 0,
    nodes: [],
  },
};

const defaultState: AppState = {
  cart: emptyCart,
  setCart: () => {
    throw new Error('setCart called outside of AppProvider');
  },
};

const AppContext = createContext<AppState>(defaultState);

interface AppProviderProps {
  children: React.ReactNode;
}

const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [cart, setCartState] = useState<Cart>(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedCart = localStorage.getItem('cart');
        return storedCart ? JSON.parse(storedCart) : emptyCart;
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
      }
    }
    return emptyCart;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('cart', JSON.stringify(cart));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cart]);

  const contextValue = useMemo(() => ({ cart, setCart: setCartState }), [cart]);

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

const useAppState = () => useContext(AppContext);

export { AppProvider, useAppState };
